import React, { useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { ContentCopy } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { PassOrderPageData, PassOut, PassService } from '../../../../api';
import { ApiError } from '../../../../api/core/ApiError';
import { useSafeExecute } from '../../../../common/apiNotifications/safeExecute';
import { useApiResponseHandlerContext } from '../../../../common/apiNotifications/useApiResponseHandlerContext';
import { ApiResponseHandlerType } from '../../../../common/apiNotifications/ApiResponseHandlerContext';
import { usePaymentCapabilities } from '../../../../common/payments/usePaymentCapabilities';
import { ColorButton } from '../../../../common/ui/ConfimationDialog';
import { CardAction, getCardAction, getCardActionBlock } from '../../utils';

type Props = {
  pass: PassOut;
  passOrderPage: PassOrderPageData | undefined;
};

export const CardOnFileSection = ({ pass, passOrderPage }: Props) => {
  const { t } = useTranslation();
  const safeExecute = useSafeExecute();
  const { showResponseNotification } = useApiResponseHandlerContext();
  const { saved_cards: supportsSavedCards } = usePaymentCapabilities();

  const [consentOpen, setConsentOpen] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [link, setLink] = useState('');

  // Older saved cards predate us storing the suffix, so it can be missing on a pass that does have a card.
  const savedCardLabel = pass.card_suffix
    ? t('passes.cardOnFile.status_saved_suffix', { suffix: pass.card_suffix })
    : t('passes.cardOnFile.status_saved');

  const block = getCardActionBlock(pass, passOrderPage, supportsSavedCards);
  const action = getCardAction(pass);

  // FastAPI serialises HTTPException(detail=...) as {"detail": ...}, so safeExecute's default
  // (error.body?.message) never matches. The server writes these as finished admin-facing
  // sentences, so an unmapped one is still readable.
  const mapCardError = (error: ApiError): string => {
    const detail = error.body?.detail;
    if (typeof detail === 'string') return detail;
    return t('passes.cardOnFile.errors.generic');
  };

  const openLink = (url: string) => {
    setLink(url);
    // The member has to open this on their own device, so the useful default is the link on the
    // clipboard ready to paste into whatever the business messages them on.
    void navigator.clipboard.writeText(url).catch(() => {});
  };

  const generate = async (withConsent: boolean) => {
    setGenerating(true);
    try {
      const result = await safeExecute({
        apiCall: () =>
          action === CardAction.Replace
            ? PassService.createPassCardUpdateLink(pass.id)
            : PassService.createMembershipTokenizationLink(pass.id, {
                email: pass.email,
                consent_accepted: withConsent,
              }),
        suppressSuccessMessage: true,
        successMessage: '',
        onErrorMessageGenerator: mapCardError,
      });
      setConsentOpen(false);
      setConsentAccepted(false);
      openLink(result.url);
    } catch {
      // safeExecute already surfaced the error; keep the consent dialog open so the admin can retry.
    } finally {
      setGenerating(false);
    }
  };

  const onCta = () => {
    if (action === CardAction.Replace) {
      void generate(false);
      return;
    }
    setConsentOpen(true);
  };

  return (
    <Grid
      container
      flexDirection="column"
      gap={0.5}
      borderRadius="8px"
      border="1px solid"
      borderColor="divider"
      p={1.5}
      mt={1}>
      <Typography fontWeight={700}>{t('passes.cardOnFile.title')}</Typography>
      <Grid container gap={0.5} alignItems="center" flexWrap="nowrap">
        <Typography variant="body2">
          {pass.has_saved_card ? savedCardLabel : t('passes.cardOnFile.status_none')}
        </Typography>
      </Grid>

      {block ? (
        <Typography variant="caption" color="text.secondary">
          {t(`passes.cardOnFile.blocked.${block}`)}
        </Typography>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary">
            {t(`passes.cardOnFile.${action}_hint`)}
          </Typography>
          <Grid container mt={0.5}>
            <LoadingButton
              variant="outlined"
              loading={generating && !consentOpen}
              onClick={onCta}
              sx={{ boxShadow: 'none' }}>
              {t(`passes.cardOnFile.${action}_cta`)}
            </LoadingButton>
          </Grid>
        </>
      )}

      {/* The migration records a save-card consent Waiver in the member's name, so the admin is
          attesting to a conversation that happened rather than agreeing on their own behalf. */}
      <Dialog open={consentOpen} onClose={() => setConsentOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('passes.cardOnFile.consent_title')}</DialogTitle>
        <DialogContent>
          <Grid container flexDirection="column" gap={1.5} mt={1}>
            <Typography variant="body2">{t('passes.cardOnFile.consent_body')}</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={consentAccepted}
                  onChange={(e) => setConsentAccepted(e.target.checked)}
                />
              }
              label={<Typography variant="body2">{t('passes.cardOnFile.consent_checkbox')}</Typography>}
            />
          </Grid>
        </DialogContent>
        <DialogActions>
          <ColorButton onClick={() => setConsentOpen(false)}>{t('cancel')}</ColorButton>
          <LoadingButton
            variant="contained"
            loading={generating}
            disabled={!consentAccepted}
            onClick={() => {
              void generate(true);
            }}
            sx={{ boxShadow: 'none' }}>
            {t('passes.cardOnFile.generate_cta')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={!!link} onClose={() => setLink('')} fullWidth maxWidth="sm">
        <DialogTitle>{t('passes.cardOnFile.link_title')}</DialogTitle>
        <DialogContent>
          <Grid container flexDirection="column" gap={1.5} mt={1}>
            <Typography variant="body2">{t('passes.cardOnFile.link_body')}</Typography>
            <TextField size="small" value={link} InputProps={{ readOnly: true }} onFocus={(e) => e.target.select()} />
            {/* Nothing changes on the pass until Grow posts the notify, which can be days later. */}
            <Typography variant="caption" color="text.secondary">
              {t('passes.cardOnFile.link_async_note')}
            </Typography>
          </Grid>
        </DialogContent>
        <DialogActions>
          <ColorButton onClick={() => setLink('')}>{t('close')}</ColorButton>
          <Button
            variant="contained"
            sx={{ boxShadow: 'none' }}
            onClick={() => {
              void navigator.clipboard
                .writeText(link)
                .then(() => showResponseNotification(t('copiedToClipboard'), ApiResponseHandlerType.SUCCESS));
            }}>
            <Grid container gap={1} alignItems="center" flexWrap="nowrap">
              <ContentCopy style={{ width: 16 }} />
              {t('copyToClipboard.copy')}
            </Grid>
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};
